import { repairLeakedTextInDictationText } from './dictationBlankBox';
// 权威实现同源：PsycheFlow-Mobile2/src/utils/repairLatexEscapes.ts
// 改前必读 AppFunctor docs/math-latex-cjk-render-playbook.md 与 dictation-blank-box-playbook.md

/** Match playbook blank strut; keep ≤1.35em so blanks stay square, not oversized. */
const DICTATION_STRUT_HEIGHT_EM = 1.35;
const DICTATION_HEIGHT_MARKER = String.raw`\rule{0pt}{1.35em}`;
/** Was 1.5 → 2.025em CJK boxes (走形). Keep 1.0 so hspace stays playbook 1.35em. */
const DICTATION_WIDTH_SCALE = 1;
/** Effective \\hspace inside each CJK blank after DICTATION_WIDTH_SCALE. */
const CJK_CHAR_HSPACE_EM = 1.35;

function formatEm(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return `${rounded}em`;
}

function buildDictationStrut(): string {
  return `\\rule{0pt}{${formatEm(DICTATION_STRUT_HEIGHT_EM)}}\\vphantom{\\text{Xy}}`;
}

function isHanCharacter(char: string): boolean {
  return /[\u3400-\u9fff]/u.test(char);
}

function isHanOnlyWord(word: string): boolean {
  const chars = [...word];
  return chars.length > 0 && chars.every(isHanCharacter);
}

function extractBracedContent(text: string, openBraceIndex: number): { inner: string; end: number } | null {
  if (text[openBraceIndex] !== '{') {
    return null;
  }

  let depth = 0;
  for (let index = openBraceIndex; index < text.length; index += 1) {
    const char = text[index];
    if (char === '{') {
      depth += 1;
    } else if (char === '}') {
      depth -= 1;
      if (depth === 0) {
        return { inner: text.slice(openBraceIndex + 1, index), end: index };
      }
    }
  }

  return null;
}

function extractPhantomWord(inner: string): string | null {
  const textMatch = inner.match(/\\phantom\{\\text\{([^}]*)\}\}/);
  if (textMatch) {
    return textMatch[1];
  }

  const plainMatch = inner.match(/\\phantom\{([^}]+)\}/);
  return plainMatch ? plainMatch[1] : null;
}

function extractHspaceEm(inner: string): number | null {
  const match = inner.match(/\\hspace\{([\d.]+)em\}/);
  if (!match) {
    return null;
  }

  const parsed = Number.parseFloat(match[1]);
  return Number.isFinite(parsed) ? parsed : null;
}

/** \\rule{Wem}{Hem} with W>0 renders as a solid black bar in KaTeX — must be repaired. */
function extractFilledSquareRuleEm(inner: string): number | null {
  const match = inner.match(/\\rule\{([\d.]+)em\}\{([\d.]+)em\}/);
  if (!match) {
    return null;
  }

  const widthEm = Number.parseFloat(match[1]);
  const heightEm = Number.parseFloat(match[2]);
  if (!Number.isFinite(widthEm) || !Number.isFinite(heightEm) || widthEm <= 0) {
    return null;
  }

  return heightEm;
}

function hasHorizontalWordPhantom(inner: string): boolean {
  return /\\phantom\{\\text\{[^}]+\}\}/.test(inner);
}

function isLatinTargetWord(word: string): boolean {
  return !isHanOnlyWord(word) && word !== 'Xy';
}

/** `$\\boxed{a}$$\\boxed{b}$` → `$\\boxed{a}$ $\\boxed{b}$`，避免 KaTeX 将相邻行内公式粘成块级。 */
export function splitAdjacentInlineMathDelimiters(text: string): string {
  return String(text || '').replace(
    /\}\$\$(\\(?:boxed|fbox)\{)/g,
    (_, open) => `}$ $${open}`,
  );
}

function isWellFormedEnglishDictationBlankInner(inner: string): boolean {
  const word = extractPhantomWord(inner);
  const hspaceEm = extractHspaceEm(inner);
  return word === 'Xy'
    && hspaceEm != null
    && hasHorizontalWordPhantom(inner)
    && /\\vphantom\{\\text\{Xy\}\}/.test(inner);
}

function isCjkDictationBlankInner(inner: string): boolean {
  const word = extractPhantomWord(inner);
  if (word && isHanOnlyWord(word)) {
    return true;
  }

  if (!inner.includes('\\rule{') && !inner.includes(DICTATION_HEIGHT_MARKER)) {
    return false;
  }

  return !hasHorizontalWordPhantom(inner) && extractHspaceEm(inner) != null;
}

function computeLatinBlankHspaceEm(word: string): number {
  const len = [...word.trim()].length;
  if (len <= 4) return 2;
  if (len <= 8) return 2.75;
  return 3.6;
}

function buildCjkCharBlankBody(): string {
  const hspaceEm = CJK_CHAR_HSPACE_EM * DICTATION_WIDTH_SCALE;
  return `${buildDictationStrut()}\\phantom{\\text{Xy}}\\hspace{${formatEm(hspaceEm)}}`;
}

function buildLatinBlankBody(word: string, hspaceEm?: number): string {
  const scaledHspace = (hspaceEm ?? computeLatinBlankHspaceEm(word)) * DICTATION_WIDTH_SCALE;
  return `${buildDictationStrut()}\\phantom{\\text{Xy}}\\hspace{${formatEm(scaledHspace)}}`;
}


function repairFilledSquareRule(inner: string): string | null {
  if (extractFilledSquareRuleEm(inner) == null) {
    return null;
  }

  const word = extractPhantomWord(inner);
  const hspaceEm = extractHspaceEm(inner);
  if (word && isLatinTargetWord(word)) {
    return buildLatinBlankBody(word, hspaceEm ?? 0.9);
  }

  return buildCjkCharBlankBody();
}

function extractBoxedExpressions(text: string): string[] {
  const boxes: string[] = [];
  let cursor = 0;
  const prefix = '\\boxed{';

  while (cursor < text.length) {
    const start = text.indexOf(prefix, cursor);
    if (start < 0) {
      break;
    }

    const braceStart = start + prefix.length - 1;
    const extracted = extractBracedContent(text, braceStart);
    if (!extracted) {
      break;
    }

    boxes.push(`${prefix}${extracted.inner}}`);
    cursor = extracted.end + 1;
  }

  return boxes;
}

/**
 * One $...$ with many \\boxed{} cannot wrap; split into $\\boxed{a}$ $\\boxed{b}$ so lines can break.
 */
function splitInlineMathByBoxChunks(inner: string): string[] {
  const boxes = extractBoxedExpressions(inner);
  if (boxes.length === 0) {
    const trimmed = inner.trim();
    return trimmed ? [trimmed] : [];
  }

  const chunks: string[] = [];
  let cursor = 0;

  for (const box of boxes) {
    const start = inner.indexOf(box, cursor);
    if (start < 0) {
      continue;
    }

    const before = inner.slice(cursor, start);
    if (before.trim()) {
      chunks.push(before);
    }
    chunks.push(`$${box}$`);
    cursor = start + box.length;
  }

  const after = inner.slice(cursor);
  if (after.trim()) {
    chunks.push(after);
  }

  return chunks;
}

const CJK_MATH_TEXT_COMMAND_PATTERN = /\\(?:text|overline|underline|mathrm|mathbf|mbox|operatorname)\{/;
const CJK_OR_FULLWIDTH_PATTERN = /[\u3400-\u9fff\u3000-\u303f\uff00-\uffef]/;

/** True when CJK remains after stripping \\text{…} / \\mathrm{…} etc. (prose leaked into math). */
function hasCjkOutsideTextCommands(inner: string): boolean {
  const stripped = String(inner || '').replace(
    /\\(?:text|overline|underline|mathrm|mathbf|mbox|operatorname)\{[^}]*\}/g,
    '',
  );
  return CJK_OR_FULLWIDTH_PATTERN.test(stripped);
}

/**
 * 模型常在中文标点前误写 `\\，` / 段末孤立 `\\`；拆出中文后残留 `\\` 会触发 katex-error（红字）。
 */
function stripOrphanLatexBackslashes(text: string): string {
  return String(text || '')
    // `\\，` / `\\。` → 标点本身
    .replace(/\\([，。；：、！？…])/g, '$1')
    // 段末孤立反斜杠（可带空白；保留 `\\\\` 换行）
    .replace(/(?<!\\)\\[ \t]*$/g, '')
    // 段首孤立 `\\ `（非命令名）
    .replace(/^\\(?=[\s]|$)/g, '');
}

/**
 * 把「公式 + 中文 + 公式」混排的 $…$ 内文拆开：中文提出去，公式段重新包 $…$。
 * 旧逻辑在无 \\boxed 时直接丢掉定界符，会把 \\le 等命令泄到正文。
 */
function splitAndRewrapMixedMath(inner: string): string {
  const parts = String(inner || '').split(/([\u3400-\u9fff\u3000-\u303f\uff00-\uffef]+)/);
  return parts.map((part) => {
    if (!part) return '';
    if (CJK_OR_FULLWIDTH_PATTERN.test(part)) {
      return part;
    }
    const cleaned = stripOrphanLatexBackslashes(part);
    const trimmed = cleaned.trim();
    if (!trimmed) return cleaned;
    // 纯标点/空白：不包公式
    if (!/[A-Za-z0-9\\]/.test(trimmed)) {
      return cleaned;
    }
    const leading = cleaned.match(/^\s*/)?.[0] || '';
    const trailing = cleaned.match(/\s*$/)?.[0] || '';
    return `${leading}$${trimmed}$${trailing}`;
  }).join('');
}

/**
 * Pull CJK / punctuation runs out of $...$ so KaTeX does not warn on unicodeTextInMathMode.
 * Keeps \\boxed / \\fcolorbox chunks inside inline math delimiters.
 * Preserves $\\text{}$, $\\overline{\\text{}}$, etc. used in math word problems.
 *
 * 扫描行内 $ 前必须先暂存 $$…$$，避免块级定界符被拆坏。
 */
export function extractCjkFromInlineMath(text: string): string {
  const displayPlaceholders: string[] = [];
  let source = String(text || '').replace(/\$\$([\s\S]+?)\$\$/g, (full) => {
    const index = displayPlaceholders.length;
    displayPlaceholders.push(full);
    return `%%PFDISPLAY${index}%%`;
  });

  source = source.replace(/\$([^$]+?)\$/g, (full, inner: string) => {
    if (!CJK_OR_FULLWIDTH_PATTERN.test(inner)) {
      return full;
    }

    // $\\text{中文}$ / $\\overline{\\text{…}}$：中文只在命令参数内 → 保留
    if (CJK_MATH_TEXT_COMMAND_PATTERN.test(inner) && !hasCjkOutsideTextCommands(inner)) {
      return full;
    }

    const boxes = extractBoxedExpressions(inner);
    if (boxes.length > 0) {
      const chunks = splitInlineMathByBoxChunks(inner);
      if (!chunks.length) return full;
      // 非 boxed 片段也可能含公式命令，再拆一次避免裸 \\le
      return chunks.map((chunk) => {
        if (chunk.startsWith('$') && chunk.endsWith('$')) return chunk;
        if (CJK_OR_FULLWIDTH_PATTERN.test(chunk) || /\\[a-zA-Z]/.test(chunk)) {
          return splitAndRewrapMixedMath(chunk);
        }
        return chunk;
      }).join('');
    }

    return splitAndRewrapMixedMath(inner);
  });

  return source.replace(/%%PFDISPLAY(\d+)%%/g, (_, indexRaw: string) => (
    displayPlaceholders[Number(indexRaw)] || ''
  ));
}

export function extractCjkFromDisplayMath(text: string): string {
  return String(text || '').replace(/\$\$([\s\S]+?)\$\$/g, (full, inner: string) => {
    if (!CJK_OR_FULLWIDTH_PATTERN.test(inner)) {
      return full;
    }

    if (CJK_MATH_TEXT_COMMAND_PATTERN.test(inner) && !hasCjkOutsideTextCommands(inner)) {
      return full;
    }

    const boxes = extractBoxedExpressions(inner);
    if (boxes.length > 0) {
      const chunks = splitInlineMathByBoxChunks(inner);
      if (!chunks.length) return full;
      return chunks.map((chunk) => {
        if (chunk.startsWith('$') && chunk.endsWith('$')) return chunk;
        if (CJK_OR_FULLWIDTH_PATTERN.test(chunk) || /\\[a-zA-Z]/.test(chunk)) {
          return splitAndRewrapMixedMath(chunk);
        }
        return chunk;
      }).join('');
    }

    // display 混排：拆成行内公式更稳
    return splitAndRewrapMixedMath(inner);
  });
}

/**
 * 兜底：正文中尚未被 $…$ 包裹的比较符命令（\\le / \\ge / \\leq …）包成行内公式。
 * 覆盖「$ 开了又因中文被拆坏」或模型直接输出裸命令的情况。
 */
export function wrapBareComparisonOperators(text: string): string {
  const source = String(text || '');
  if (!/\\(?:leq|geq|neq|le|ge|ne)\b/.test(source)) {
    return source;
  }

  return mapPlainOutsideMath(source, wrapBareComparisonInPlain);
}

function wrapBareComparisonInPlain(plain: string): string {
  // 形如 2A-9 \le 50 或 A \le 29.5 —— 捕获命令左右一小段代数
  return plain.replace(
    /(?<!\$)((?:[0-9A-Za-z)）\]}][0-9A-Za-z+\-*/^=().\s]*)?)\\(leq|geq|neq|le|ge|ne)\b((?:\s*[0-9A-Za-z(（\[{][0-9A-Za-z+\-*/^=().]*)?)/g,
    (_full, left: string, cmd: string, right: string) => {
      const inner = `${left || ''}\\${cmd}${right || ''}`.trim();
      if (!inner) return _full;
      return `$${inner}$`;
    },
  );
}

/**
 * 兜底：裸 \\binom / \\frac / \\sqrt（及左右紧贴的代数片段）包成 $…$。
 * 常见泄露：解的个数为\\binom{k+3}{3}；(r-1)\\binom{k+3}{3}+1；(r-1)\\times\\binom{k+3}{3}
 */
export function wrapBareStructCommands(text: string): string {
  const source = String(text || '');
  if (!/\\(?:binom|frac|sqrt)\b/.test(source)) {
    return source;
  }

  return mapPlainOutsideMath(source, wrapBareStructInPlain);
}

/**
 * 兜底：正文中裸 \\times / \\div / \\cdot 等算术式包成 $…$。
 * MinerU / 工厂结构聚合常见：19 \times 125 \times 8 = 19000\div 9 \div 4
 */
export function wrapBareArithmeticCommands(text: string): string {
  const source = String(text || '');
  if (!/\\(?:times|div|cdot|pm|mp)\b/.test(source)) {
    return source;
  }
  return mapPlainOutsideMath(source, wrapBareArithmeticInPlain);
}

function wrapBareArithmeticInPlain(plain: string): string {
  // Normalize unicode ops so mixed MinerU lines (× + \div) wrap as one formula.
  const normalized = plain
    .replace(/\u00d7/g, ' \\times ')
    .replace(/\u00f7/g, ' \\div ')
    .replace(/\s{2,}/g, ' ');

  const latexAtom =
    String.raw`(?:\\(?:times|div|cdot|pm|mp)\b|\\(?:frac|binom)\{[^{}]+\}\{[^{}]+\}|\\sqrt(?:\[[^\]]*\])?\{[^{}]+\}|[0-9]+(?:\.[0-9]+)?|[A-Za-z]|[（(][^）)\n]{0,80}[）)]|[【\[][^】\]\n]{0,40}[】\]])`;
  const glue = String.raw`\s*(?:\\(?:times|div|cdot|pm|mp)|[=+\-])\s*`;
  const pattern = new RegExp(`${latexAtom}(?:${glue}${latexAtom})+`, 'g');

  return normalized.replace(pattern, (expr) => {
    if (!/\\(?:times|div|cdot|pm|mp|frac|binom|sqrt)\b/.test(expr)) return expr;
    return `$${expr.trim()}$`;
  });
}

/**
 * 兜底：裸下标/上标（及含它们的短等式）包成 $…$。
 * 常见泄露：题干已有 `$S5(i)$` 时，整段不会被 wrapBareLatexMath 包住，
 * `a_i = a_{i+5}` 仍以源码留在正文。
 */
export function wrapBareScriptAtoms(text: string): string {
  const source = String(text || '');
  if (!/[_^]/.test(source)) {
    return source;
  }

  return mapPlainOutsideMath(source, wrapBareScriptsInPlain);
}

function wrapBareScriptsInPlain(plain: string): string {
  // a_i / a_{i+5} / a^2 / S_n(i)；未加大括号的下标最长 3，避免误伤 snake_case
  // 注意：含 | 的片段必须整体包进 (?:…)，否则与后续 op/atom 拼接时优先级会错
  const scriptedId = String.raw`(?:[A-Za-z](?:\d{0,2})?(?:_(?:\{[^}]+\}|[A-Za-z0-9]{1,3})|\^(?:\{[^}]+\}|[A-Za-z0-9]{1,3}))+(?![A-Za-z0-9])(?:\([^()]*\))?)`;
  const plainId = String.raw`(?:[A-Za-z]\d{0,2}(?![A-Za-z0-9])(?:\([^()]*\))?|\d+(?:\.\d+)?)`;
  const atom = `(?:${scriptedId}|${plainId})`;
  const op = String.raw`\s*(?:=|\\neq|\\leq|\\geq|\\approx|≠|≤|≥|\+|−|-)\s*`;
  const pattern = new RegExp(
    `(?<![\\\\$\\w])((?:${scriptedId}(?:${op}${atom})+)|(?:${plainId}${op}${scriptedId}(?:${op}${atom})*)|(?:${scriptedId}))`,
    'g',
  );

  return plain.replace(pattern, (_full, expr: string) => `$${expr}$`);
}

function wrapBareStructInPlain(plain: string): string {
  // 代数原子：数字字母括号运算符，或 \\times / \\cdot 等
  const atom = String.raw`(?:\\(?:times|cdot|div|pm|mp)\b|[0-9A-Za-z()（）\[\]+\-*/^=])`;
  const struct = String.raw`\\(?:binom\{[^{}]+\}\{[^{}]+\}|frac\{[^{}]+\}\{[^{}]+\}|sqrt\{[^{}]+\})`;
  const pattern = new RegExp(`(?<!\\$)(${atom}*)(${struct})(${atom}*)`, 'g');

  return plain.replace(pattern, (_full, left: string, cmd: string, right: string) => (
    `$${left}${cmd}${right}$`
  ));
}

const WRAP_BARE_MATH_ENV =
  /^(?:array|matrix|pmatrix|bmatrix|Bmatrix|vmatrix|Vmatrix|cases|aligned|align\*?|gather\*?|equation\*?)$/;

/**
 * 裸 \\begin{array|matrix|cases|aligned|…}…\\end{…} 包成 $$…$$。
 * 常见泄露：题干里已有行内 $a$ 时，整段不会被 wrapBareLatexMath 包住，array 表就原样漏出。
 */
export function wrapBareLatexEnvironments(text: string): string {
  const source = String(text || '');
  if (!/\\begin\{/.test(source)) {
    return source;
  }
  // `$…\begin{aligned}…$` 会被当成行内数学 → KaTeX 失败露红字源码；先剥掉这类定界符。
  const unwrapped = source.replace(
    /(?<!\$)\$(?!\$)([^$]*\\begin\{[a-zA-Z*]+\}[^$]*)\$(?!\$)/g,
    (_full, inner: string) => String(inner || '').trim(),
  );
  return mapPlainOutsideMath(unwrapped, wrapBareEnvsInPlain);
}

function wrapBareEnvsInPlain(plain: string): string {
  // KaTeX 对嵌套场景更稳的是 aligned（而非 align）
  const normalized = plain
    .replace(/\\begin\{align\*?\}/g, '\\begin{aligned}')
    .replace(/\\end\{align\*?\}/g, '\\end{aligned}');
  return normalized.replace(
    /\\begin\{([a-zA-Z*]+)\}([\s\S]*?)\\end\{\1\}/g,
    (full, envName: string) => {
      if (!WRAP_BARE_MATH_ENV.test(envName)) {
        return full;
      }
      if (/^\$\$/.test(full) || /\$\$$/.test(full)) return full;
      return `$$${full}$$`;
    },
  );
}

/** 对已有 $…$ / $$…$$ / \[…\] / \(…\) 之外的纯文本片段应用 transform */
function mapPlainOutsideMath(source: string, transform: (plain: string) => string): string {
  const parts: string[] = [];
  let last = 0;
  const mathRe = /\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\$[^$]+\$|\\\([\s\S]+?\\\)/g;
  let match: RegExpExecArray | null;
  while ((match = mathRe.exec(source)) != null) {
    parts.push(transform(source.slice(last, match.index)));
    parts.push(match[0]);
    last = match.index + match[0].length;
  }
  parts.push(transform(source.slice(last)));
  return parts.join('');
}

export function splitDictationInlineMathForWrap(text: string): string {
  return String(text || '').replace(/\$([^$\n]+?)\$/g, (full, inner: string) => {
    if (!inner.includes('\\boxed{')) {
      return full;
    }

    const boxes = extractBoxedExpressions(inner);
    if (boxes.length <= 1) {
      return full;
    }

    const firstBoxAt = inner.indexOf('\\boxed{');
    const lastBox = boxes[boxes.length - 1];
    const lastBoxAt = inner.lastIndexOf(lastBox);
    const leading = inner.slice(0, firstBoxAt);
    const trailing = inner.slice(lastBoxAt + lastBox.length);

    return `${leading}${boxes.map((box) => `$${box}$`).join(' ')}${trailing}`;
  });
}

function repairLeakedTextInBoxedInner(inner: string): string {
  let result = String(inner || '').replace(/\\vphantom\{\\text\{([^}]+)\}\}/g, (match, word: string) => (
    word === 'Xy' ? match : '\\vphantom{\\text{Xy}}'
  ));

  result = result.replace(/\\phantom\{\\text\{([^}]+)\}\}/g, (match, word: string) => (
    word === 'Xy' ? match : '\\phantom{\\text{Xy}}'
  ));

  result = result.replace(/\\text\{([^}]+)\}/g, (match, word: string, offset: number, full: string) => {
    if (word === 'Xy') {
      return match;
    }

    const before = full.slice(Math.max(0, offset - 10), offset);
    if (before.endsWith('\\phantom{') || before.endsWith('\\vphantom{')) {
      return match;
    }

    return '';
  });

  if (!/\\phantom\{\\text\{Xy\}\}/.test(result)) {
    const leakedWord = [...String(inner || '').matchAll(/\\text\{([^}]+)\}/g)]
      .map((entry) => entry[1])
      .find((word) => word && word !== 'Xy');
    const hspaceEm = extractHspaceEm(inner) ?? computeLatinBlankHspaceEm(String(leakedWord || ''));
    return buildLatinBlankBody(String(leakedWord || ''), hspaceEm);
  }

  return result;
}

function isPlainLatinDictationWord(inner: string): boolean {
  const trimmed = inner.trim();
  if (!trimmed || /\\/.test(trimmed)) {
    return false;
  }

  return /^[A-Za-z][A-Za-z' -]*$/.test(trimmed);
}

function isPlainCjkDictationChar(inner: string): boolean {
  const trimmed = inner.trim();
  if (!trimmed || /\\/.test(trimmed)) {
    return false;
  }

  const chars = [...trimmed];
  return chars.length === 1 && isHanCharacter(chars[0]);
}

function processDictationBoxInner(inner: string): string {
  if (isWellFormedEnglishDictationBlankInner(inner)) {
    return inner;
  }

  if (isPlainLatinDictationWord(inner)) {
    return buildLatinBlankBody(inner.trim());
  }

  if (isPlainCjkDictationChar(inner)) {
    return buildCjkCharBlankBody();
  }

  const repairedFilledSquare = repairFilledSquareRule(inner);
  if (repairedFilledSquare != null) {
    return repairedFilledSquare;
  }

  if (isCjkDictationBlankInner(inner)) {
    return buildCjkCharBlankBody();
  }

  const word = extractPhantomWord(inner);
  const hspaceEm = extractHspaceEm(inner);
  if (word && hspaceEm != null && isLatinTargetWord(word)) {
    return buildLatinBlankBody(word, hspaceEm);
  }

  if (/\\text\{/.test(inner) && !hasHorizontalWordPhantom(inner)) {
    return repairLeakedTextInBoxedInner(inner);
  }

  if (!word || hspaceEm == null) {
    return inner;
  }

  return buildLatinBlankBody(word, hspaceEm);
}

function renderDictationBox(openWrapper: string, inner: string, closeWrapper: string): string {
  const nextInner = processDictationBoxInner(inner);
  return `${openWrapper}${nextInner}${closeWrapper}`;
}

function replaceWrappedDictationBoxes(
  text: string,
  openWrapper: string,
  closeWrapper: string,
): string {
  let result = '';
  let cursor = 0;

  while (cursor < text.length) {
    const start = text.indexOf(openWrapper, cursor);
    if (start < 0) {
      result += text.slice(cursor);
      break;
    }

    result += text.slice(cursor, start);
    const braceStart = start + openWrapper.length - 1;
    const extracted = extractBracedContent(text, braceStart);
    if (!extracted) {
      result += text.slice(start);
      break;
    }

    const tokenLength = extracted.end - start + 1;
    if (isInlineMathWrapped(text, start, tokenLength)) {
      const nextInner = processDictationBoxInner(extracted.inner);
      result += `${openWrapper}${nextInner}}`;
    } else {
      result += renderDictationBox(openWrapper, extracted.inner, closeWrapper);
    }
    cursor = extracted.end + 1;
  }

  return result;
}

function isInsideInlineMathDelimiters(full: string, tokenStart: number): boolean {
  let inlineOpen = false;

  for (let index = 0; index < tokenStart; index += 1) {
    const char = full[index];
    if (char === '\\') {
      index += 1;
      continue;
    }
    if (char !== '$') {
      continue;
    }

    if (full[index + 1] === '$') {
      const closeIndex = full.indexOf('$$', index + 2);
      if (closeIndex === -1) {
        index += 1;
        continue;
      }
      index = closeIndex + 1;
      continue;
    }

    inlineOpen = !inlineOpen;
  }

  return inlineOpen;
}

/** True when \\boxed is already inside $...$ (including multi-box yuwen dictation spans). */
function isInlineMathWrapped(full: string, start: number, tokenLength: number): boolean {
  if (full[start - 1] === '$') {
    return true;
  }
  if (full[start + tokenLength] === '$') {
    return true;
  }
  return isInsideInlineMathDelimiters(full, start);
}

function wrapBareBoxPrefix(source: string, prefix: string): string {
  let result = '';
  let cursor = 0;

  while (cursor < source.length) {
    const start = source.indexOf(prefix, cursor);
    if (start < 0) {
      result += source.slice(cursor);
      break;
    }

    result += source.slice(cursor, start);
    const braceStart = start + prefix.length - 1;
    const extracted = extractBracedContent(source, braceStart);
    if (!extracted) {
      result += source.slice(start);
      break;
    }

    const token = `${prefix}${extracted.inner}}`;
    const tokenLength = extracted.end - start + 1;
    result += isInlineMathWrapped(source, start, tokenLength) ? token : `$${token}$`;
    cursor = extracted.end + 1;
  }

  return result;
}

/**
 * English dictation FC sometimes emits bare \\boxed{...} without $...$ delimiters.
 * KaTeX only typesets math inside delimiters, so wrap each bare box chunk.
 */
export function wrapBareDictationMathDelimiters(text: string): string {
  let source = String(text || '');
  if (!/\\(?:boxed|fbox)\{/.test(source)) {
    return source;
  }

  source = wrapBareBoxPrefix(source, '\\boxed{');
  source = wrapBareBoxPrefix(source, '\\fbox{');
  return source;
}

/** Normalize dictation blanks: CJK uses fixed-width boxes; Latin keeps phantom-based width. */
export function enhanceDictationBlankBoxes(text: string): string {
  let enhanced = replaceWrappedDictationBoxes(text, '\\boxed{', '}');
  enhanced = replaceWrappedDictationBoxes(enhanced, '\\fbox{', '}');
  enhanced = replaceWrappedDictationBoxes(enhanced, '\\fcolorbox{black}{white}{', '}');
  return enhanced;
}

/** `$\\binom{...}$` / 裸 `\\\\frac`：模型或入库多重转义后 KaTeX 会当成换行+裸命令 */
export function collapseExcessLatexCommandBackslashes(text: string): string {
  return String(text || '').replace(
    /\\{2,}(binom|frac|sqrt|times|cdot|div|leq|geq|neq|approx|left|right|begin|end|text|mathrm|mathbf|mathbb|overline|underline|boxed|sum|prod|int|lim|pmod|equiv|triangle|alpha|beta|gamma|delta|theta|pi|sigma|mu|infty|to|tan|sin|cos|log|ln|exp|hat|bar|vec|dot|phantom|vphantom|hspace|rule|bigl|bigr|Bigl|Bigr|biggl|biggr|ge|le|ne|pm|mp|in|subset|cup|cap)\b/g,
    '\\$1',
  );
}

/** `$...$` 内换行 + `times` 残片（`\times` 被 JSON/`\n` 展开拆坏后的典型形态） */
export function repairCorruptedTimesInMathText(text: string): string {
  let next = collapseExcessLatexCommandBackslashes(String(text || ''))
    .replace(/\u0009imes/g, '\\times')
    // 误叠定界符：$$$$formula$$$$ → $$formula$$
    // 注意：String.replace 替换串中 `$$` 表示字面量 `$`，故要写 `$$$$` 才能得到 `$$`
    .replace(/\${4,}/g, '$$$$');

  const fixTimesFragment = (chunk: string) => chunk
    .replace(/\\ntimes/g, '\\times')
    // JSON `\b` → BS：`\binom` 变 U+0008inom
    .replace(/\u0008inom\b/g, '\\binom')
    // k\n times / (r-1)\n times\binom —— times 后可接空白、括号、CJK 或 LaTeX 命令
    .replace(/(?<=[0-9A-Za-z)）])\s*[\n\u0009]+\s*times(?=[\s（(\u3400-\u9fff]|\\)/g, ' \\times')
    .replace(/(?<=[0-9A-Za-z)）])\s*[\n\u0009]+\s*imes(?=[\s（(\u3400-\u9fff]|\\)/g, ' \\times')
    .replace(/(?<=[0-9A-Za-z)）])\s+times(?=[\s（(\u3400-\u9fff]|\\)/g, ' \\times')
    .replace(/(?<=[0-9A-Za-z)）])times(?=\\)/g, ' \\times')
    .replace(/\s+imes(?=[\s（(\u3400-\u9fff]|\\)/g, ' \\times')
    // 裸 times抽屉数（无反斜杠、无括号）
    .replace(/(?<=[0-9A-Za-z)）=\-\s])times([\u3400-\u9fff]+)/g, ' \\times(\\text{$1})')
    .replace(/\\times([\u3400-\u9fff]+)/g, '\\times(\\text{$1})');

  const collapseNewlinesInMath = (inner: string) => fixTimesFragment(inner)
    .replace(/\u00d7/g, '\\times')
    .replace(/\u00f7/g, '\\div')
    .replace(/\s*\n+\s*/g, ' ');

  // 必须先处理 $$…$$，再用 $…$；否则单 $ 会吃掉块级定界符的一侧
  const displayPlaceholders: string[] = [];
  next = next.replace(/\$\$([\s\S]+?)\$\$/g, (_full, inner: string) => {
    const index = displayPlaceholders.length;
    displayPlaceholders.push(`$$${collapseNewlinesInMath(inner)}$$`);
    return `%%PFDISPLAY${index}%%`;
  });

  next = next.replace(/\$([^$]+?)\$/g, (_full, inner: string) => {
    return `$${collapseNewlinesInMath(inner)}$`;
  });

  next = next.replace(/%%PFDISPLAY(\d+)%%/g, (_, indexRaw: string) => (
    displayPlaceholders[Number(indexRaw)] || ''
  ));

  // 裸文本
  next = fixTimesFragment(next);

  // 典型抽屉原理公式：仅包裹尚未在 $…$ 内的裸式
  next = next.replace(
    /(?<!\$)N\s*=\s*(?:k\s*\\times\s*[（(][^$\n）)]*[）)]|\([^$\n)]+\)\s*\\times\s*(?:\\binom\{[^}]+\}\{[^}]+\}|\\text\{[^}]+\}|[\u3400-\u9fff]+|[（(][^$\n）)]*[）)]))\s*(?:\+\s*1)?(?!\$)/gi,
    (match) => `$${match.trim()}$`,
  );

  // 其它含 \times 的裸公式片段
  next = next.replace(
    /(?<!\$)(?<![$\w\\])([0-9A-Za-z=+\-*(（][^$\n]{0,140}?\\times(?:\s*\\binom\{[^}]+\}\{[^}]+\}|\s*[（(][^$\n）)]*[）)]|\s*\\text\{[^}]+\}|\s*[\u3400-\u9fff]+)[^$\n]{0,40}?)(?=[，。；、]|$|\s+的|\s+再|\s+以及)/g,
    (match) => `$${match.trim()}$`,
  );

  // 简式：9 = 3 \times 3（仅定界符外，避免拆坏已有 $…$）
  next = mapPlainOutsideMath(next, (plain) => plain.replace(
    /(?<!\{)(?<![$\w\\])(\d+\s*=\s*\d+\s*\\times\s*\d+)/g,
    (match) => `$${match.trim()}$`,
  ));

  return next;
}

/**
 * 将 JSON 字面量 `\\n` 转为换行，但不拆 LaTeX 命令（`\newline`、误写的 `\ntimes` 等）。
 * 须在 repairCorruptedTimesInMathText 之后调用。
 */
export function expandJsonNewlineEscapes(text: string): string {
  return String(text || '').replace(/\\n(?![a-zA-Z])/g, '\n');
}

/**
 * Extract FC / 模型常把中文下标写成 `$v_{$车$}$`。
 * 内层 `$` 会切断 `$…$` 扫描，整段公式无法进 KaTeX。
 * `_{$车$}` → `_{\text{车}}`；`_{$n$}` → `{n}`。
 */
export function unwrapNestedScriptMathDollars(text: string): string {
  let next = String(text || '');
  for (let i = 0; i < 6; i += 1) {
    const updated = next.replace(
      /([_^])\{?\$([^$\n]{1,24})\$\}?/g,
      (_full, script: string, inner: string) => {
        const trimmed = inner.trim();
        if (!trimmed) return `${script}{}`;
        if (CJK_OR_FULLWIDTH_PATTERN.test(trimmed)) {
          return `${script}{\\text{${trimmed}}}`;
        }
        return `${script}{${trimmed}}`;
      },
    );
    if (updated === next) break;
    next = updated;
  }
  return next;
}

/**
 * MinerU / 聚合拼接常见破损：
 * - `$…=36$0 \times`（定界符后粘连数字）
 * - `\time` 被截成非 `\times`
 * - 未闭合的 `$…`
 */
export function repairBrokenInlineMathDelimiters(text: string): string {
  let next = unwrapNestedScriptMathDollars(String(text || ''));
  // Truncated \times (not \timeout)
  next = next.replace(/\\time(?![a-zA-Z])/g, '\\times');
  // `$formula$123` → `$formula$ 123` so trailing bare ops can be re-wrapped
  next = next.replace(/\$([^$\n]+?)\$(\d)/g, (_full, body: string, digit: string) => (
    `$${body}$ ${digit}`
  ));
  // Unclosed trailing `$…` without a closer: drop the orphan opener
  next = next.replace(/(^|[^$])\$([^$\n]*)$/g, (_full, prefix: string, body: string) => {
    if (!/\\[a-zA-Z]/.test(body)) return _full;
    return `${prefix}${body}`;
  });
  return next;
}

/** Repair LaTeX commands broken by JSON.parse (\\f, \\b) or downstream whitespace normalization. */
export function repairLatexEscapes(text: string): string {
  let normalized = repairCorruptedTimesInMathText(
    splitAdjacentInlineMathDelimiters(repairBrokenInlineMathDelimiters(String(text || ''))),
  )
    // JSON/LLM over-escape: `\\times` → `\times` (keep `\\` + space/newline/`&` as aligned breaks)
    .replace(/\\\\([a-zA-Z]+)/g, '\\$1')
    .replace(/\u000ccolorbox/g, '\\fcolorbox')
    .replace(/\u0008oxed/g, '\\boxed')
    .replace(/\u0008egin/g, '\\begin')
    .replace(/\u000dule/g, '\\rule')
    .replace(/\u000bphantom/g, '\\vphantom')
    .replace(/\u0009ext/g, '\\text')
    .replace(/\u0009imes/g, '\\times')
    .replace(/\$\s*colorbox\{black\}\{white\}/g, '$\\fcolorbox{black}{white}')
    .replace(/\u000c/g, ' ')
    .replace(/\u000b/g, ' ')
    // 先清掉中文标点前的误写反斜杠，再拆 CJK，避免 `$…\\，$` → 红字
    .replace(/\\([，。；：、！？…])/g, '$1')
    // 裸 `\\ 9 = …`：误写换行反斜杠，勿留在正文
    .replace(/(?<![$\w\\])\\\s+(?=\d)/g, ' ');

  if (normalized.includes('\\boxed{') || normalized.includes('\\fbox{')) {
    normalized = repairLeakedTextInDictationText(normalized);
  }

  normalized = wrapBareDictationMathDelimiters(normalized);
  normalized = enhanceDictationBlankBoxes(normalized);
  normalized = splitDictationInlineMathForWrap(normalized);
  // 先 display 再 inline，避免 $$…$$ 被行内 $ 扫描误伤
  normalized = extractCjkFromInlineMath(extractCjkFromDisplayMath(normalized));
  // 拆 CJK 后仍可能残留 `$…\\$`，再清一遍段末孤立反斜杠
  normalized = normalized.replace(/\$([^$]+?)\$/g, (_full, inner: string) => (
    `$${stripOrphanLatexBackslashes(inner)}$`
  ));
  normalized = wrapBareComparisonOperators(normalized);
  normalized = wrapBareLatexEnvironments(normalized);
  normalized = wrapBareStructCommands(normalized);
  normalized = wrapBareArithmeticCommands(normalized);
  normalized = wrapBareScriptAtoms(normalized);
  // wrap 后偶发 `$\frac{1}{45}$\ 9`：定界符后的孤立反斜杠
  return normalized.replace(/\$\\\s+/g, '$ ');
}
